/** Saved-version transport only. Never falls back to a source intake on failure. */
import { request } from '../../workspace/api.js';
import type { ProductionLineBriefFields } from '@mouja/domain/production-line-brief';
import type { BusinessProfileDraft } from '@mouja/domain/analysis';
import type { ResearchReference } from './researchPresentation.js';
import type { FirstIdeaView } from './firstIdeaApi.js';
export interface SavedBrief extends ProductionLineBriefFields {
  versionId: string;
  versionNumber: number;
  productionLineId: string;
  analysisRunId: string;
  createdAt: string;
  currentStage: string;
  executionState: string;
  queueState: string | null;
}
export interface SavedDirection {
  artifactId: string;
  payload: {
    title: string;
    angle: string;
    objective: string;
    limitations: string[];
    contentIdea: {
      basis: 'LIVE_REFERENCE' | 'SAVED_REFERENCE' | 'BUSINESS_BASED' | 'TOPIC_BASED';
      hook: string;
      creativeTreatment: string | null;
      whyRelevant: string | null;
      outputLocale: 'en' | 'ar';
      references: ResearchReference[];
    };
  };
}
export interface SavedBriefJourney {
  firstIdea?: FirstIdeaView | null;
  canEdit: boolean;
  kind: 'SAVED_BRIEF';
  productionLineId: string;
  isCurrent: boolean;
  currentVersionId: string;
  brief: SavedBrief;
  sourceProfile: { artifactId: string; payload: BusinessProfileDraft };
  ideas: { handoffArtifactId: string | null; directions: SavedDirection[] };
  script: { publicId: string; state: string } | null;
}
export interface SavedScriptView {
  selectedIdea?: { title: string; hook: string; outputLocale: 'en' | 'ar' };
  publicId: string;
  state: string;
  isCurrent: boolean;
  canApprove: boolean;
  preparationAuthorized: boolean;
  resultHash: string | null;
  scriptHash: string | null;
  approval: { approvedAt: string } | null;
  script: {
    title: string;
    hook: string;
    outputLocale: 'en' | 'ar';
    scenes: {
      number: number;
      startSeconds: number;
      endSeconds: number;
      spokenText: string;
      onScreenText: string | null;
      visualDescription: string;
      claimSourceFields: string[];
    }[];
    cta: { text: string; destination: string } | null;
  } | null;
  review: {
    disposition: string;
    report: {
      units: {
        id: string;
        spans: {
          text: string;
          classification: string;
          reason: string;
        }[];
      }[];
      combined: { classification: string; reason: string };
    };
  } | null;
  production: { status: 'NOT_CONFIGURED'; mediaAuthorized: false };
}
const base = '/merchant/production-line-briefs';
export const fetchSavedBrief = (
  lineId: string,
  version?: string,
  signal?: AbortSignal,
  ideaRevision?: string,
) =>
  request<SavedBriefJourney | { kind: 'LEGACY' }>(
    'GET',
    `/merchant/production-lines/${encodeURIComponent(lineId)}/saved-brief${version || ideaRevision ? `?${new URLSearchParams({ ...(version ? { version } : {}), ...(ideaRevision ? { ideaRevision } : {}) }).toString()}` : ''}`,
    signal ? { signal } : {},
  );
export const fetchBriefHistory = (lineId: string) =>
  request<{ versions: SavedBrief[] }>(
    'GET',
    `/merchant/production-lines/${encodeURIComponent(lineId)}/brief-history`,
  );
export const saveBrief = (
  mode: 'create' | 'edit' | 'duplicate',
  body: unknown,
  idempotencyKey: string,
) =>
  request<{ productionLineId: string; briefVersionId: string }>(
    'POST',
    mode === 'create' ? base : `${base}/${mode}`,
    { body, idempotencyKey },
  );
export const selectSavedIdea = (body: unknown, idempotencyKey: string) =>
  request<{ selectionId: string }>('POST', '/merchant/saved-brief-selections', {
    body,
    idempotencyKey,
  });
export const requestSavedScript = (selectionId: string, idempotencyKey: string) =>
  request<{ run: { publicId: string } }>('POST', '/merchant/saved-script-runs', {
    body: { selectionId },
    idempotencyKey,
  });
export const fetchSavedScript = (publicId: string) =>
  request<SavedScriptView>(
    'GET',
    `/merchant/saved-script-runs/${encodeURIComponent(publicId)}`,
  );
export const approveSavedScript = (view: SavedScriptView, idempotencyKey: string) =>
  request(
    'POST',
    `/merchant/saved-script-runs/${encodeURIComponent(view.publicId)}/approve`,
    {
      body: { resultHash: view.resultHash, scriptHash: view.scriptHash },
      idempotencyKey,
    },
  );
